/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Datetime from "react-datetime";
import moment from "moment-timezone";
import "react-datetime/css/react-datetime.css";
import styled from "styled-components";
import { SaveUser } from "../../redux/actions/UserActions";
import IntraverseLogoFull from "../landingPages/intraverseLandingPage/IntraverseHomeImages/IntraverseLogoFull.png";
import Footer from "../../components/layout/footer/Footer";
// API & Utils
import { RegisterUser, emailExistsChecker, usernameExistsChecker } from "../../api/UserController";
import AuthSocial from "../../api/AuthSocial";
import { validateUsername, nameValidation, isvalidPassword, isValidEmail } from "../../utils/generalUtils";
import BackgroundContainer from "../../components/common/Backgrounds/BackgroundContainer";
import { CheckBoxMain } from "../../components/common/checkBox/CheckBoxMain";
import { setColor, setFont } from "../../assets/styles/styles";
import PasswordChecker from "./PasswordChecker";
import { Input } from "../../components/common/input/Input";
import Button from "../../components/common/button/Button";
import { countryOptions } from "../../constants";
import LoadingIcon from "../../components/common/loading/LoadingIcon";
import BirthdayIcon from "../../assets/images/icons/UC_Sprite_Icon_Birthday.png";

export default function RegisterUserPage() {
    const dispatch = useDispatch();

    // Form data states
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [country, setCountry] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [username, setUsername] = useState("");
    const [birthday, setBirthday] = useState("");
    const [isBirthdayInputValid, setIsBirthdayInputValid] = useState(true);

    // Page Section state handling
    const [section, setSection] = useState("name");
    const [isLoading, setIsLoading] = useState(false);

    // Error states
    const [error, setError] = useState("");
    const [nameError, setNameError] = useState("");
    const [nameValidError, setNameValidError] = useState("");
    const [usernameError, setUsernameError] = useState("");
    const [termsError, setTermsError] = useState("");
    const [isNewsChecked, setIsNewsChecked] = useState(false);
    const [isTermsChecked, setIsTermsChecked] = useState(false);
    const [emailExistsBlock, setEmailExistsBlock] = useState(false);
    const [usernameExists, setUsernameExists] = useState(false);

    // Current date for birthday validation
    const now = useMemo(() => new Date(), []);

    useEffect(() => {
        if (
            !nameValidation(firstName.trim(), lastName.trim()) &&
            firstName.trim().length > 0 &&
            lastName.trim().length > 0
        ) {
            setNameValidError("Your name should only contain letters and/or spaces");
        } else {
            setNameValidError("");
        }
        if (firstName.trim() !== "" && lastName.trim() !== "" && country !== "") {
            setNameError("");
        } else setNameError("*Please fill out all fields");
    }, [firstName, country, lastName]);

    const handleEmailSubmit = async () => {
        try {
            const exists = await emailExistsChecker(email.toLowerCase());
            if (exists) {
                setError("This email is already in use, please sign in, or try another email.");
                setEmailExistsBlock(true);
            } else if (isValidEmail(email) && isvalidPassword(password) && !exists) {
                setError("");
                setTermsError("");
                setEmailExistsBlock(false);
                setSection("username");
            } else if (!isValidEmail(email)) {
                setError("Please enter a valid email address.");
            } else if (!isvalidPassword(password)) {
                setError("Please select a valid password before continuing.");
            }
        } catch {
            setError("An error occurred, please reload the page and try again.");
        }
    };

    const validBirthdate = (current) => current.isBefore(moment());

    const handleDateChange = (date) => {
        if (moment.isMoment(date)) {
            setBirthday(date.format("YYYY-MM-DD"));
        }
    };

    const handleEmailChange = async (e) => {
        const email = e.target.value.trim();
        setEmail(email);
        const exists = await emailExistsChecker(email.toLowerCase());
        if (exists) {
            setError("This email is already in use, please sign in, or try another email.");
            setEmailExistsBlock(true);
        } else {
            setError("");
            setEmailExistsBlock(false);
        }
    };

    // Function to convert birthday format from DD-MM-YYYY to YYYY-MM-DD and must set passed date to be DD-MM-YYYY format
    const convertToBackendBirthdayFormat = (birthday) => {
        if (!birthday || birthday.length !== 10) return "";
        const [dd, mm, yyyy] = birthday.split("-");
        return `${yyyy}-${mm}-${dd}`;
    };

    const handleTermsSubmit = async (event) => {
        event.preventDefault();
        setTermsError("");
        setIsLoading(true);

        if (!isTermsChecked) {
            setTermsError("Please accept our terms & conditions before signing up");
            setIsLoading(false);
            return;
        }

        const birthdayFormat = convertToBackendBirthdayFormat(birthday);

        const res = await RegisterUser(
            firstName.trim(),
            lastName.trim(),
            email,
            password,
            username,
            birthdayFormat,
            isNewsChecked,
            country,
            ""
        );

        if (res !== null) {
            setIsLoading(false);
            dispatch(
                SaveUser(
                    res.user.email,
                    res.user.displayName,
                    res.token,
                    res.refreshToken,
                    res.userId,
                    res.userRoles[0],
                    res.isMerchant,
                    res.expiration,
                    true,
                    res.user.userBirthday,
                    res.isUserOwnerOfClient
                )
            );
        } else {
            setTermsError("Error: There was an issue with registration, please go back to try again.");
            setIsLoading(false);
        }
    };

    const handleUsernameSubmit = () => {
        if (username.length === 0) {
            setUsernameError("Please enter a username before continuing.");
        }
        if (!validateUsername(username) && username.length > 2) {
            setUsernameError(
                "Username must start with a letter, can only contain letters, numbers, underscores, or hyphens, and be between 3 and 20 characters long."
            );
        } else if (usernameExists) {
            setUsernameError("Username already exists, please try a unique username.");
        } else {
            setUsernameError("");
            setTermsError("");
            setSection("terms");
        }
    };

    const handleUsernameChange = (event) => {
        const newUsername = event.target.value;
        setUsername(newUsername);

        // Check if username is valid
        if (!validateUsername(newUsername) && newUsername.length > 2) {
            setUsernameError(
                "Username must start with a letter, can only contain letters, numbers, underscores, or hyphens, and be between 3 and 20 characters long."
            );
        } else if (validateUsername(newUsername)) {
            usernameExistsChecker(newUsername).then((res) => {
                setUsernameExists(res);
                setUsernameError("Username already exists, please try a unique username.");
            });
        } else {
            setUsernameError("");
        }
    };

    const handleNewsCheckboxChange = () => {
        setIsNewsChecked(!isNewsChecked);
    };
    const handleTermsCheckboxChange = () => {
        setIsTermsChecked(!isTermsChecked);
    };

    const handleBirthdayInput = (e) => {
        let value = e.target.value.replace(/[^\d-]/g, "");

        // Auto-insert dashes for DD-MM-YYYY
        if (value.length === 2 && birthday.length === 1) value += "-";
        if (value.length === 5 && birthday.length === 4) value += "-";
        if (value.length === 3 && value[2] !== "-") value = `${value.slice(0, 2)}-${value.slice(2)}`;
        if (value.length === 6 && value[5] !== "-") value = `${value.slice(0, 5)}-${value.slice(5)}`;
        value = value.slice(0, 10);

        setBirthday(value);

        let valid = false;
        if (value.length === 10) {
            const [dd, mm, yyyy] = value.split("-").map(Number);
            const now = new Date();

            if (mm < 1 || mm > 12) {
                valid = false;
                return;
            }
            const dateObj = new Date(yyyy, mm - 1, dd);

            valid =
                dateObj.getFullYear() === yyyy &&
                dateObj.getMonth() === mm - 1 &&
                dateObj.getDate() === dd &&
                dateObj <= now &&
                yyyy >= 1900;

            const thirteenYearsAgo = new Date(now.getFullYear() - 13, now.getMonth(), now.getDate());
            if (dateObj > thirteenYearsAgo) valid = false;
        }

        setIsBirthdayInputValid(valid);
    };

    return (
        <BackgroundContainer loginBorder>
            <HeaderDiv>
                <Link to="/">
                    <LoginLogo src={IntraverseLogoFull} alt="Company Logo" />
                </Link>
                <Link to="/login">
                    <CreateAccountButton>SIGN IN</CreateAccountButton>
                </Link>
            </HeaderDiv>
            <CustomContainer>
                {/* NAME AND COUNTRY PAGE */}

                {section === "name" && (
                    <>
                        <StepText>STEP 1 OF 4</StepText>
                        <LoginTitle>WHAT'S YOUR NAME?</LoginTitle>
                        <LoginSubtitle>
                            We will keep this information private and use it to verify your identity when contacting
                            Intraverse.
                        </LoginSubtitle>
                        <InputContainer>
                            <InputLabel>Country</InputLabel>
                            <CountrySelect value={country} onChange={(e) => setCountry(e.currentTarget.value)}>
                                <option value="" disabled hidden>
                                    Select a country
                                </option>
                                {countryOptions.map((country) => (
                                    <option key={country} value={country}>
                                        <p style={{ margin: "5px", width: "100px" }}> {country}</p>
                                    </option>
                                ))}
                            </CountrySelect>
                            <InputLabel>First Name</InputLabel>
                            <Input
                                variant="LoginInput"
                                type="text"
                                value={firstName}
                                onChangeHandler={(e) => setFirstName(e.target.value)}
                                placeholder="First Name"
                            />
                            <InputLabel>Last Name</InputLabel>
                            <Input
                                variant="LoginInput"
                                type="text"
                                value={lastName}
                                onChangeHandler={(e) => setLastName(e.target.value)}
                                placeholder="Last Name"
                            />
                        </InputContainer>
                        {nameError && <p style={{ color: setColor.errorColor }}>{nameError}</p>}
                        {nameValidError && <p style={{ color: setColor.errorColor }}>{nameValidError}</p>}
                        <Button
                            variant={"PinkButton"}
                            buttonWidth={"100%"}
                            margin={"1.5rem 0 1.5rem 0"}
                            label={"Continue to Email"}
                            style={{
                                background:
                                    nameError === "" && nameValidError === "" && country !== ""
                                        ? setColor.pinkButtonBgGradient
                                        : setColor.loadingButtonBg,
                                color:
                                    nameError === "" && nameValidError === "" && country !== ""
                                        ? setColor.hightlight
                                        : setColor.iconColor,
                            }}
                            onClick={
                                nameError === "" && nameValidError === "" && country !== ""
                                    ? () => setSection("email")
                                    : null
                            }
                        />
                        <AuthSocial origin={"loginPage"} />
                    </>
                )}

                {/* EMAIL AND PASSWORD PAGE */}

                {section === "email" && (
                    <>
                        <StepText>STEP 2 OF 4</StepText>
                        <LoginTitle>WHAT'S YOUR EMAIL?</LoginTitle>
                        <LoginSubtitle>
                            The email you provide below will be used to log in Intraverse on desktop and mobile.
                        </LoginSubtitle>
                        <InputContainer autoComplete="off">
                            <input type="email" style={{ display: "none" }} autoComplete={"email"} />
                            <input type="password" style={{ display: "none" }} autoComplete={"new-password"} />
                            <InputLabel>Email</InputLabel>
                            <Input
                                variant="LoginInput"
                                type="email"
                                value={email}
                                onChangeHandler={handleEmailChange}
                                // onChangeHandler={(e) => setEmail(e.target.value)}
                                placeholder="Email"
                            />
                            <InputLabel>Password</InputLabel>
                            <Input
                                variant="LoginInput"
                                type="password"
                                value={password}
                                onChangeHandler={(event) => {
                                    setPassword(event.target.value);
                                }}
                                placeholder="Password"
                            />
                            {error && <p style={{ color: setColor.errorColor, alignSelf: "flex-start" }}>{error}</p>}

                            <PasswordChecker password={password} />
                        </InputContainer>
                        <Button
                            variant={"PinkButton"}
                            buttonWidth={"100%"}
                            margin={"1.5rem 0 0.7rem 0"}
                            label={"Continue to Username"}
                            style={{
                                background:
                                    isValidEmail(email) && isvalidPassword(password) && !emailExistsBlock
                                        ? setColor.pinkButtonBgGradient
                                        : setColor.loadingButtonBg,
                                color:
                                    isValidEmail(email) && isvalidPassword(password) && !emailExistsBlock
                                        ? setColor.hightlight
                                        : setColor.iconColor,
                            }}
                            onClick={() => {
                                handleEmailSubmit();
                            }}
                        />
                        <StepText
                            style={{
                                color: setColor.iconColor3,
                                cursor: "pointer",
                                marginTop: "0.5rem",
                                marginBottom: "0.6rem",
                            }}
                            onClick={() => setSection("name")}
                        >
                            Back to step 1
                        </StepText>
                    </>
                )}

                {/* USERNAME PAGE */}

                {section === "username" && (
                    <>
                        <StepText>STEP 3 OF 4</StepText>
                        <LoginTitle>Create your username & Select your birthdate</LoginTitle>
                        <LoginSubtitle>
                            Pick a name for your Intraverse identity and tell us your birthdate. You can always update
                            it later!
                        </LoginSubtitle>
                        <InputContainer>
                            <InputLabel>Username</InputLabel>
                            <Input
                                type="text"
                                value={username}
                                variant="LoginInput"
                                onChangeHandler={handleUsernameChange}
                                placeholder="Username"
                                maxLength={20}
                            />
                        </InputContainer>
                        {(validateUsername(username) && !usernameExists) ||
                            (usernameError !== "" && <p style={{ color: setColor.errorColor }}>{usernameError}</p>)}
                        <InputLabel>Birthdate</InputLabel>
                        <DateInputContainer>
                            <BirthdayIconStyled src={BirthdayIcon} alt="Birthday Icon" />
                            <TextBirthdayInput
                                type="text"
                                value={birthday || ""}
                                onChange={handleBirthdayInput}
                                placeholder="DD-MM-YYYY"
                            />
                        </DateInputContainer>

                        {!isBirthdayInputValid && (
                            <p style={{ color: setColor.errorColor }}>{"Select valid Birthday"}</p>
                        )}
                        <Button
                            variant={"PinkButton"}
                            buttonWidth={"100%"}
                            margin={"1.5rem 0 0.7rem 0"}
                            label={"Continue to Last Step"}
                            disabled={
                                !validateUsername(username) ||
                                usernameExists ||
                                birthday === "" ||
                                !isBirthdayInputValid
                            }
                            style={{
                                background:
                                    validateUsername(username) &&
                                    !usernameExists &&
                                    birthday !== "" &&
                                    isBirthdayInputValid
                                        ? setColor.pinkButtonBgGradient
                                        : setColor.loadingButtonBg,
                                color:
                                    validateUsername(username) &&
                                    !usernameExists &&
                                    birthday !== "" &&
                                    isBirthdayInputValid
                                        ? setColor.hightlight
                                        : setColor.iconColor,
                            }}
                            onClick={() => {
                                handleUsernameSubmit();
                            }}
                        />
                        <StepText
                            style={{
                                color: setColor.accent4,
                                cursor: "pointer",
                                marginTop: "0.7rem",
                                marginBottom: "0.7rem",
                            }}
                            onClick={() => setSection("email")}
                        >
                            Back to step 2
                        </StepText>
                    </>
                )}

                {/* TERMS & CONDITIONS PAGE */}

                {section === "terms" && (
                    <SubmitForm onSubmit={handleTermsSubmit}>
                        <StepText style={{ marginTop: "-1rem" }}>STEP 4 OF 4</StepText>
                        <LoginTitle>REVIEW OUR TERMS</LoginTitle>
                        <LoginSubtitle>You’re almost done! Please review our terms and opt-ins below.</LoginSubtitle>
                        <CheckboxContainer>
                            <TermsText>
                                <CheckBoxMain
                                    width="30px"
                                    height="30px"
                                    checked={isNewsChecked}
                                    onChange={handleNewsCheckboxChange}
                                />
                                <p style={{ marginLeft: "15px" }} onClick={handleNewsCheckboxChange}>
                                    {" "}
                                    Receive news, special offers, event information, and feedback surveys by email from
                                    Intraverse.
                                </p>
                            </TermsText>
                            <TermsText>
                                <CheckBoxMain
                                    width="30px"
                                    height="30px"
                                    checked={isTermsChecked}
                                    onChange={handleTermsCheckboxChange}
                                />
                                <p style={{ marginLeft: "15px" }} onClick={handleTermsCheckboxChange}>
                                    I acknowledge that I have read and understood{" "}
                                    <a
                                        style={{ textDecoration: "none", color: setColor.accent4, fontWeight: 700 }}
                                        href="https://terrazero.com/privacy-policy"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Intraverse Online Privacy Policy
                                    </a>{" "}
                                    and
                                    <a
                                        style={{ textDecoration: "none", color: setColor.accent4, fontWeight: 700 }}
                                        href="https://terrazero.com/privacy-policy/"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {" "}
                                        End User License Agreement
                                    </a>
                                    .
                                </p>
                            </TermsText>
                        </CheckboxContainer>
                        {termsError && <p style={{ color: setColor.errorColor }}>{termsError}</p>}
                        <ContinueButton
                            style={{
                                background: isTermsChecked ? setColor.pinkButtonBgGradient : setColor.loadingButtonBg,
                                color:
                                    isValidEmail(email) && isvalidPassword(password)
                                        ? setColor.hightlight
                                        : setColor.iconColor,
                            }}
                            type="submit"
                        >
                            {isLoading ? (
                                <div>
                                    <LoadingIcon />
                                </div>
                            ) : (
                                "CREATE MY ACCOUNT"
                            )}
                        </ContinueButton>
                        <StepText
                            style={{ color: setColor.accent4, cursor: "pointer", margin: "0rem 0 1rem 0" }}
                            onClick={() => setSection("username")}
                        >
                            Back to step 3
                        </StepText>
                    </SubmitForm>
                )}
                <Footer />
            </CustomContainer>
        </BackgroundContainer>
    );
}

// ----------------------------------------------------------------------

const CustomContainer = styled.div`
    width: 100%;
    margin-left: auto;
    box-sizing: border-box;
    margin-right: auto;
    display: block;
    max-width: 600px;

    @media (min-width: 768px) {
        margin-top: -4rem;
        padding-left: 24px;
        padding-right: 24px;
    }
`;
const LoginLogo = styled.img`
    display: flex;
    align-self: flex-start;
    width: 250px;
    height: auto;
    margin-bottom: 30px;
    margin-top: 10px;
    margin-left: 10px;

    @media (max-width: 768px) {
        align-self: center;
        margin-top: 0px;
        margin-right: 0px;
        margin-left: 0px;
    }
`;
const HeaderDiv = styled.div`
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    width: 100%;
    margin-bottom: 40px;
    margin-right: 10px;
    margin-left: 10px;

    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
        margin-left: 0px;
        margin-top: 10px;
    }

    @media (min-width: 768px) and (max-width: 1024px) {
        flex-direction: row;
        justify-content: space-between;
    }
`;

const LoginTitle = styled.h3`
    color: ${setColor.accent4};
    text-align: center;
    font-family: ${setFont.barlowCondensed};
    font-size: 50px;
    font-weight: 700;
    line-height: normal;
    text-transform: uppercase;
    padding: 10px 0 10px 0;

    @media (max-width: 960px) {
        font-size: 28px;
        margin-top: 10px;
    }

    @media (min-width: 960px) and (max-width: 1440px) {
        font-size: 40px;
    }
`;

const LoginSubtitle = styled.p`
    color: ${setColor.circularProgress};
    text-align: center;
    font-family: ${setFont.barlow};
    font-size: 20px;
    font-weight: 400;
    margin: 1rem 0;
    @media (max-width: 960px) {
        margin: 1.5rem -10px;
        font-size: 16px;
    }
`;
const CreateAccountButton = styled.button`
    border-radius: 5px;
    border: 2px solid ${setColor.iconColor3};
    display: inline-flex;
    padding: 15px 35px;
    justify-content: center;
    align-items: center;
    background-color: transparent;
    color: ${setColor.iconColor3};
    text-align: center;
    font-family: ${setFont.barlow};
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    margin-right: 1rem;
    margin-top: 10px;
    cursor: pointer;

    @media (max-width: 768px) {
        width: 100%;
        align-self: center;
        margin-right: 0rem;
    }

    @media (min-width: 768px) and (max-width: 1440px) {
        width: fit-content;
        margin-top: 10px;
    }
`;

const ContinueButton = styled.button`
    border-radius: 5px;
    width: 100%;
    border: none;
    display: inline-flex;
    padding: 15px 35px;
    justify-content: center;
    align-items: center;
    background-color: ${setColor.loadingButtonBg};
    color: ${setColor.iconColor};
    text-align: center;
    font-family: ${setFont.barlow};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    cursor: pointer;

    @media (max-width: 768px) {
        width: 100%;
        align-self: center;
        margin-bottom: 1rem;
        font-size: 14px;
        padding: 15px 10px;
    }

    @media (min-width: 768px) and (max-width: 1440px) {
        width: 100%;
        margin-top: 10px;
    }
`;

const InputLabel = styled.p`
    color: ${setColor.circularProgress};
    font-family: "barlow", sans-serif;
    font-size: 14px;
    font-style: normal;
    line-height: normal;
    margin: 4px 0 -5px 5px;
`;

const InputContainer = styled.form`
    display: flex;
    flex-direction: column;
`;
const StepText = styled.p`
    color: ${setColor.nextStepText};
    text-align: center;
    font-family: ${setFont.barlowCondensed};
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: uppercase;
    margin-top: -20px;
`;
const CountrySelect = styled.select`
    flex: 1;
    background: ${setColor.inputBgColor};
    border: 2px solid ${setColor.loadingButtonBg};
    border-radius: 5px;
    outline: none;
    padding: 0.5rem 1rem;
    margin: 0.5rem 0rem;
    font-size: 18px;
    font-style: italic;
    font-family: ${setFont.barlow};
    color: ${setColor.iconColor};
    width: 100%;
    height: 70px;
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    text-align: left;
    flex-direction: column;
`;

const TermsText = styled.p`
    color: ${setColor.circularProgress};
    text-align: left;
    font-family: ${setFont.barlow};
    font-size: 20px;
    font-weight: 400;
    margin: 0.7rem 0;
    display: flex;
    flex-direction: row;

    @media (max-width: 320px) {
        width: 110%;
        margin-left: -10px;
        font-size: 18px;
        margin-top: 0rem;
    }

    & > a {
        color: ${setColor.accent4};
        font-weight: 700;
        cursor: pointer;
    }
`;
const DateInputContainer = styled.div`
    position: relative;
    flex-direction: row;
    -webkit-box-align: center;
    align-items: center;
    background: rgb(27, 27, 35);
    border: 2px solid rgb(80, 76, 92);
    border-radius: 5px;
    width: 100%;
    height: 45px;
    margin: 1rem 0rem;
`;

const BirthdayIconStyled = styled.img`
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    pointer-events: none;
    user-select: none;
`;

const CustomDatetime = styled(Datetime)`
    .rdtDisabled {
        cursor: not-allowed !important;
        color: #ccc;
    }
    .rdt {
        background: rgb(27, 27, 35);
    }
    .rdtPicker {
        background: rgb(27, 27, 35);
        width: 100%;
        color: ${setColor.inputText};
    }
    .rdtDays,
    .rdtTime {
        background: rgb(27, 27, 35);
    }
    .rdtDay,
    .rdtMonth,
    .rdtPrev,
    .rdtNext,
    .rdtPicker th,
    .rdtYear {
        color: ${setColor.inputText};
        &:hover {
            color: black;
            background-color: ${setColor.grey1};
            cursor: pointer;
        }
    }
    input {
        padding: 10px 10px 10px 40px;
        color: ${setColor.inputText};
        width: 100%;
        background-color: transparent;
        border: 0px solid transparent;
        margin: 0;
        margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
        outline: none;
        box-shadow: none;
        &:disabled {
            background-color: ${setColor.grey1};
        }
    }
`;

const TextBirthdayInput = styled.input`
    padding: 10px 10px 10px 40px;
    color: white;
    width: 100%;
    background-color: transparent;
    border: 0px solid transparent;
    outline: none;
`;

const SubmitForm = styled.form``;
